import { pushDeviceLabel, type PushSubscriptionInput } from "@fts/core";

/**
 * Browser side of web push (ADR 0019). The permission prompt is only ever opened from a user click
 * (subscribeThisDevice is called from a button handler), never on page load.
 */
export type PushEnvironment =
  /** Push can be enabled on this device. */
  | "ok"
  /** iPhone/iPad: web push works only in the app added to the home screen (iOS 16.4+). */
  | "ios_install"
  /** Notification permission was blocked for this site in the browser settings. */
  | "denied"
  /** The browser has no service worker / Push API, or the page is not served over HTTPS. */
  | "unsupported";

type NavLike = { userAgent: string; maxTouchPoints?: number; standalone?: boolean; serviceWorker?: unknown };
type WinLike = { isSecureContext?: boolean; PushManager?: unknown; Notification?: { permission: string }; matchMedia?: (q: string) => { matches: boolean } };

export function isIos(nav: NavLike): boolean {
  // iPadOS reports a Mac user agent; touch points tell them apart.
  return /iPhone|iPad|iPod/.test(nav.userAgent) || (/Macintosh/.test(nav.userAgent) && (nav.maxTouchPoints ?? 0) > 1);
}

export function isStandalone(nav: NavLike, win: WinLike): boolean {
  return nav.standalone === true || !!win.matchMedia?.("(display-mode: standalone)").matches;
}

export function pushEnvironment(nav: NavLike = navigator as unknown as NavLike, win: WinLike = window as unknown as WinLike): PushEnvironment {
  if (isIos(nav) && !isStandalone(nav, win)) return "ios_install";
  if (!win.isSecureContext || !nav.serviceWorker || !win.PushManager || !win.Notification) return "unsupported";
  if (win.Notification.permission === "denied") return "denied";
  return "ok";
}

export class PushClientError extends Error {
  constructor(readonly code: "denied" | "no_service_worker" | "subscribe_failed") {
    super(code);
    this.name = "PushClientError";
  }
}

export function b64urlToBytes(s: string): Uint8Array<ArrayBuffer> {
  const bin = atob(s.replace(/-/g, "+").replace(/_/g, "/") + "===".slice((s.length + 3) % 4));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function bytesToB64url(buf: ArrayBuffer | null): string {
  if (!buf) return "";
  let s = "";
  for (const b of new Uint8Array(buf)) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Some browsers pad base64url in toJSON(); the database expects it without padding. */
export const unpad = (s: string | undefined): string => (s ?? "").replace(/=+$/, "");

async function registration(): Promise<ServiceWorkerRegistration> {
  const reg = "serviceWorker" in navigator ? await navigator.serviceWorker.getRegistration() : undefined;
  // The service worker is registered only in the built console (not in `vite dev`).
  if (!reg) throw new PushClientError("no_service_worker");
  return reg;
}

/** This browser's current subscription endpoint, or null. */
export async function currentEndpoint(): Promise<string | null> {
  try {
    const reg = "serviceWorker" in navigator ? await navigator.serviceWorker.getRegistration() : undefined;
    const sub = await reg?.pushManager?.getSubscription();
    return sub?.endpoint ?? null;
  } catch {
    return null;
  }
}

/** Ask for permission (from a click only), subscribe with the server's VAPID key and return what the database stores. */
export async function subscribeThisDevice(publicKey: string): Promise<PushSubscriptionInput> {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new PushClientError("denied");
  const reg = await registration();
  let sub = await reg.pushManager.getSubscription();
  // A subscription made with another (rotated) VAPID key no longer works: replace it.
  if (sub && bytesToB64url(sub.options.applicationServerKey) !== unpad(publicKey)) {
    await sub.unsubscribe().catch(() => undefined);
    sub = null;
  }
  try {
    sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64urlToBytes(publicKey) });
  } catch {
    throw new PushClientError("subscribe_failed");
  }
  const json = sub.toJSON();
  return { endpoint: sub.endpoint, p256dh: unpad(json.keys?.p256dh), auth: unpad(json.keys?.auth), label: pushDeviceLabel(navigator.userAgent) };
}

/** Unsubscribe this browser; returns the endpoint that was removed (to delete the row), or null. */
export async function unsubscribeThisDevice(): Promise<string | null> {
  try {
    const reg = await registration();
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return null;
    const endpoint = sub.endpoint;
    await sub.unsubscribe().catch(() => undefined);
    return endpoint;
  } catch {
    return null;
  }
}

type PushRows = { listPushDevices(): Promise<{ id: string; endpoint: string }[]>; removePushDevice(id: string): Promise<void> };
type PushLocal = { currentEndpoint(): Promise<string | null>; unsubscribe(): Promise<string | null> };

/**
 * Sign-out on a shared device (ADR 0019): this browser must stop receiving the user's alarms. Unsubscribes
 * the browser immediately and, in parallel, deletes the matching push_subscriptions row while the session
 * still exists. Best effort and time-bounded: never blocks or fails the sign-out.
 */
export async function releasePushOnSignOut(
  rows: PushRows,
  local: PushLocal = { currentEndpoint, unsubscribe: unsubscribeThisDevice },
  timeoutMs = 3000,
): Promise<void> {
  const work = (async () => {
    const endpoint = await local.currentEndpoint();
    if (!endpoint) return;
    // The browser is unsubscribed at once, in parallel with the server row delete: a slow or hanging
    // network (or a tab closed right after signing out) must not leave the browser subscribed. A row
    // the delete could not reach is removed later by the push service's 404/410.
    const server = (async () => {
      const row = (await rows.listPushDevices()).find((d) => d.endpoint === endpoint);
      if (row) await rows.removePushDevice(row.id);
    })();
    await Promise.allSettled([server, local.unsubscribe()]);
  })().catch(() => undefined);
  let timer: ReturnType<typeof setTimeout> | undefined;
  await Promise.race([work, new Promise<void>((resolve) => (timer = setTimeout(resolve, timeoutMs)))]);
  clearTimeout(timer);
}

/** base64url (unpadded) VAPID key this browser's subscription was made with, or null (none / unknown). */
export async function currentSubscriptionKey(): Promise<string | null> {
  try {
    const reg = "serviceWorker" in navigator ? await navigator.serviceWorker.getRegistration() : undefined;
    const sub = await reg?.pushManager?.getSubscription();
    const key = sub?.options?.applicationServerKey ?? null;
    return key ? bytesToB64url(key) : null;
  } catch {
    return null;
  }
}
